/** A refused row's plain sentence when the server sent no `refusal_title`, by its code. The row's own message is never shown. */
const REFUSAL_TITLE: Record<string, string> = {
    delivery_overdue: 'This load’s delivery date has passed.',
    truck_overlap: 'The truck is on another load at that time.',
    driver_status_conflict: 'A driver on this truck has a status that blocks the load.',
    driver_status_changed: 'A driver’s status changed. Try again.',
    truck_in_transit_elsewhere: 'The truck is already in transit on another load.',
    truck_terminated: 'This truck is no longer active.',
    driver_inactive: 'A driver on this truck is inactive.',
    load_changed: 'The load changed. Try again.',
    not_assigned_to_that_truck: 'The load is not on that truck.',
    load_locked: 'The load is locked by another change. Try again.',
    load_route_incomplete: 'The load’s route is incomplete.',
    not_found: 'The truck or the load could not be found.',
    load_is_split: 'This load is split and cannot be assigned here.',
}

const FALLBACK = 'This change was refused.'

/** The plain sentence for a refusal text that names a known code, else undefined: a raw message is never shown. */
export const plainRefusalOf = (text: string): string | undefined => {
    const code = Object.keys(REFUSAL_TITLE).find((key) => new RegExp(`\\b${key}\\b`).test(text))
    return code ? REFUSAL_TITLE[code] : undefined
}

/** The sentence a refused row shows: the server's `refusal_title`, else the words for its code. */
export const refusalTitleOf = (row?: { code?: string; refusal_title?: string }): string =>
    row?.refusal_title?.trim() || (row?.code ? REFUSAL_TITLE[row.code] : undefined) || FALLBACK

/** The fewest invoices a new factoring batch takes, as on the Invoices page (the prepare card refuses a smaller one). */
export const FACTORING_BATCH_MIN = 2

/** The factoring batch's minimum, in the words the card, the drawer and a refused batch all say. */
export const FACTORING_BATCH_MIN_SENTENCE = `A factoring batch needs at least ${FACTORING_BATCH_MIN} invoices.`

/** The prepare card's refusals the chat knows, by a phrase of the card's own text, and the plain sentence each is shown as. */
const BATCH_REFUSALS: ReadonlyArray<{ match: RegExp; sentence: string }> = [
    { match: /needs at least \d+ invoices/i, sentence: FACTORING_BATCH_MIN_SENTENCE },
    { match: /batch name .* already used/i, sentence: 'That batch name is already used. Try again with another name.' },
    { match: /no free batch name/i, sentence: 'No batch name was free. Prepare the batch on the Invoices page.' },
]

const BATCH_FALLBACK = 'The factoring batch was not prepared. Try again, or prepare it on the Invoices page.'

/**
 * The plain sentence a refused or failed factoring batch is shown as. The card's and the model's text is never shown to the user: a text
 * the chat does not know is logged as it came and said in the generic words.
 */
export const plainBatchRefusalOf = (text: string): string => {
    const known = BATCH_REFUSALS.find(({ match }) => match.test(text))
    if (known) return known.sentence
    console.error(text)
    return BATCH_FALLBACK
}
